// M95 (2026-04-26) — Admin AI Pricing page.
//
// 2 section:
//   1. Bảng giá Model — table list + edit inline + add new + button "🔄 Cập nhật từ LiteLLM"
//      → preview diff (added/changed/skipped_manual) → admin apply
//   2. Tỷ giá USD → VND — current value + button "🔄 Cập nhật từ Vietcombank"
//      → preview (old vs new) → admin apply
//
// Sync online dùng pricingSyncService (LiteLLM + VCB API).
// Manual edit set source='manual' → không bị overwrite bởi auto-sync sau này.

import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { useAuthStore } from '../lib/auth.js';
import { showConfirm } from '../lib/dialogs.js';

const PROVIDER_OPTIONS = ['anthropic', 'openai', 'deepseek', 'gemini'];
const SOURCE_LABEL = {
  manual:    { text: 'Tự nhập', color: 'bg-amber-100 text-amber-800 border-amber-300' },
  m94_seed:  { text: 'Seed mặc định', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  litellm:   { text: 'LiteLLM', color: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
};

function fmtUsd(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (n === 0) return '0';
  if (n < 0.0001) return n.toExponential(2);
  return n.toFixed(5).replace(/\.?0+$/, '');
}

function fmtVnd(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return Number(v).toLocaleString('vi-VN') + 'đ';
}

function fmtDt(s) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleString('vi-VN', { hour12: false });
  } catch { return s; }
}

export default function AdminAiPricingPage() {
  const nav = useNavigate();
  const { user } = useAuthStore();
  // M146: manager với quyền 'ai_pricing' cũng truy cập được
  const isAdmin = (function () {
    if (user?.role === 'admin') return true;
    if (user?.role === 'manager' && Array.isArray(user?.permissions)) return user.permissions.includes('ai_pricing');
    return false;
  })();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  // Add/Edit form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ provider: 'anthropic', model: '', price_per_1k_in: '', price_per_1k_out: '', notes: '' });

  // Sync preview state
  const [litellmPreview, setLitellmPreview] = useState(null);
  const [vcbPreview, setVcbPreview] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Chỉ admin được truy cập trang này');
      nav('/', { replace: true });
    }
  }, [isAdmin, nav]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.adminAiPricing();
      setData(res);
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Không tải được bảng giá');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ----- CRUD -------------------------------------------------------------

  function startAdd() {
    setEditingId(null);
    setForm({ provider: 'anthropic', model: '', price_per_1k_in: '', price_per_1k_out: '', notes: '' });
    setShowAddForm(true);
  }

  function startEdit(row) {
    setEditingId(row.id);
    setForm({
      provider: row.provider,
      model: row.model,
      price_per_1k_in: String(row.price_per_1k_in),
      price_per_1k_out: String(row.price_per_1k_out),
      notes: row.notes || '',
    });
    setShowAddForm(true);
  }

  function cancelForm() {
    setShowAddForm(false);
    setEditingId(null);
  }

  async function submitForm(e) {
    e.preventDefault();
    if (busy) return;
    const priceIn = Number(form.price_per_1k_in);
    const priceOut = Number(form.price_per_1k_out);
    if (!Number.isFinite(priceIn) || priceIn < 0) { toast.error('Giá input không hợp lệ'); return; }
    if (!Number.isFinite(priceOut) || priceOut < 0) { toast.error('Giá output không hợp lệ'); return; }
    if (!form.model.trim()) { toast.error('Thiếu tên model'); return; }
    setBusy(true);
    try {
      if (editingId) {
        await api.adminAiPricingUpdate(editingId, {
          price_per_1k_in: priceIn,
          price_per_1k_out: priceOut,
          notes: form.notes.trim() || null,
        });
        toast.success('Đã cập nhật');
      } else {
        await api.adminAiPricingCreate({
          provider: form.provider,
          model: form.model.trim(),
          price_per_1k_in: priceIn,
          price_per_1k_out: priceOut,
          notes: form.notes.trim() || null,
        });
        toast.success('Đã thêm model mới');
      }
      cancelForm();
      await load();
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Lỗi lưu');
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(row) {
    const ok = await showConfirm({
      title: 'Xoá giá model',
      message: `Xoá ${row.provider} / ${row.model}? Hành động này không thể undo.`,
      confirmText: 'Xoá',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await api.adminAiPricingDelete(row.id);
      toast.success('Đã xoá');
      await load();
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Xoá thất bại');
    }
  }

  // ----- LiteLLM Sync -----------------------------------------------------

  async function fetchLitellmPreview() {
    setBusy(true);
    setLitellmPreview(null);
    try {
      const res = await api.adminAiPricingSyncLitellmPreview();
      setLitellmPreview(res);
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Fetch LiteLLM thất bại');
    } finally {
      setBusy(false);
    }
  }

  async function applyLitellmSync() {
    if (!litellmPreview) return;
    const ok = await showConfirm({
      title: 'Áp dụng giá từ LiteLLM',
      message: `Sẽ thêm ${litellmPreview.added.length} model mới + cập nhật ${litellmPreview.changed.length} model đổi giá. ${litellmPreview.skipped_manual.length} model "Tự nhập" được giữ nguyên (bỏ qua). Tiếp tục?`,
      confirmText: 'Áp dụng',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.adminAiPricingSyncLitellmApply();
      toast.success(`Đã thêm ${res.n_added}, cập nhật ${res.n_changed} model`);
      setLitellmPreview(null);
      await load();
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Apply thất bại');
    } finally {
      setBusy(false);
    }
  }

  // ----- VCB Sync ---------------------------------------------------------

  async function fetchVcbPreview() {
    setBusy(true);
    setVcbPreview(null);
    try {
      const res = await api.adminAiPricingSyncVcbPreview();
      setVcbPreview(res);
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Fetch tỷ giá thất bại');
    } finally {
      setBusy(false);
    }
  }

  async function applyVcbSync() {
    if (!vcbPreview) return;
    if (vcbPreview.diff === 0) {
      toast('Tỷ giá không đổi — không cần áp dụng', { icon: 'ℹ️' });
      return;
    }
    const direction = vcbPreview.diff > 0 ? 'tăng' : 'giảm';
    const ok = await showConfirm({
      title: 'Cập nhật tỷ giá USD/VND',
      message: `Tỷ giá ${direction} ${Math.abs(vcbPreview.diff).toLocaleString('vi-VN')}đ (${vcbPreview.diff_pct?.toFixed(2)}%) từ ${vcbPreview.old_rate.toLocaleString('vi-VN')} → ${vcbPreview.new_rate.toLocaleString('vi-VN')}đ. Áp dụng?`,
      confirmText: 'Áp dụng',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await api.adminAiPricingSyncVcbApply();
      toast.success(`Đã cập nhật: ${res.new_rate.toLocaleString('vi-VN')}đ`);
      setVcbPreview(null);
      await load();
    } catch (err) {
      toast.error(err?.data?.error || err?.message || 'Apply thất bại');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="text-slate-500">Đang tải...</div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <header className="space-y-2">
        <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Trang admin</Link>
        <h1 className="text-2xl font-bold">Bảng giá AI & tỷ giá USD/VND</h1>
        <p className="text-sm text-slate-600">
          Quản lý giá API các model AI (Claude, GPT, DeepSeek, Gemini...) + tỷ giá USD/VND để tính cost theo VND.
          Sync online từ <a href="https://github.com/BerriAI/litellm" target="_blank" rel="noopener" className="text-blue-600 hover:underline">LiteLLM</a> (~500 model) và Vietcombank.
        </p>
      </header>

      {/* ============================================================ */}
      {/* SECTION 2: Tỷ giá USD/VND (đặt trên cùng vì user thường check) */}
      {/* ============================================================ */}
      <section className="border rounded-lg p-5 bg-blue-50/50">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-semibold mb-1">💱 Tỷ giá USD → VND</h2>
            <div className="text-3xl font-bold text-blue-700">
              {data.usd_to_vnd.value.toLocaleString('vi-VN')} <span className="text-base font-normal text-slate-500">VND/USD</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">Cập nhật cuối: {fmtDt(data.usd_to_vnd.updated_at)}</div>
          </div>
          <button
            type="button"
            onClick={fetchVcbPreview}
            disabled={busy}
            className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium disabled:opacity-50"
          >
            🔄 Cập nhật từ Vietcombank
          </button>
        </div>

        {vcbPreview && (
          <div className="mt-4 p-4 bg-white border rounded-md space-y-2">
            <div className="text-sm font-semibold">Preview ({vcbPreview.source}):</div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <div className="text-slate-500 text-xs">Hiện tại</div>
                <div className="font-semibold">{vcbPreview.old_rate.toLocaleString('vi-VN')}đ</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">Mới</div>
                <div className="font-semibold text-blue-700">{vcbPreview.new_rate.toLocaleString('vi-VN')}đ</div>
              </div>
              <div>
                <div className="text-slate-500 text-xs">Thay đổi</div>
                <div className={`font-semibold ${vcbPreview.diff > 0 ? 'text-red-600' : vcbPreview.diff < 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {vcbPreview.diff > 0 ? '+' : ''}{vcbPreview.diff.toLocaleString('vi-VN')}đ
                  {vcbPreview.diff_pct != null && ` (${vcbPreview.diff_pct.toFixed(2)}%)`}
                </div>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={applyVcbSync}
                disabled={busy || vcbPreview.diff === 0}
                className="px-4 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50"
              >
                Áp dụng
              </button>
              <button
                type="button"
                onClick={() => setVcbPreview(null)}
                className="px-4 py-1.5 rounded-md bg-slate-200 hover:bg-slate-300 text-sm"
              >
                Huỷ
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ============================================================ */}
      {/* SECTION 1: Bảng giá Model                                    */}
      {/* ============================================================ */}
      <section className="border rounded-lg p-5 bg-white">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
          <h2 className="text-lg font-semibold">📊 Bảng giá Model AI ({data.pricing.length})</h2>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={fetchLitellmPreview}
              disabled={busy}
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium disabled:opacity-50"
            >
              🔄 Cập nhật từ LiteLLM
            </button>
            <button
              type="button"
              onClick={startAdd}
              disabled={busy}
              className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 font-medium disabled:opacity-50"
            >
              + Thêm model
            </button>
          </div>
        </div>

        {/* LiteLLM preview */}
        {litellmPreview && (
          <div className="mb-4 p-4 bg-emerald-50 border border-emerald-200 rounded-md space-y-2">
            <div className="text-sm font-semibold text-emerald-900">
              Preview LiteLLM ({litellmPreview.total_in_source} model trong nguồn):
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div className="bg-white p-2 rounded border">
                <div className="text-xs text-slate-500">➕ Thêm mới</div>
                <div className="text-2xl font-bold text-emerald-700">{litellmPreview.added.length}</div>
              </div>
              <div className="bg-white p-2 rounded border">
                <div className="text-xs text-slate-500">🔄 Đổi giá</div>
                <div className="text-2xl font-bold text-blue-700">{litellmPreview.changed.length}</div>
              </div>
              <div className="bg-white p-2 rounded border">
                <div className="text-xs text-slate-500">⏭ Bỏ qua (Tự nhập)</div>
                <div className="text-2xl font-bold text-amber-700">{litellmPreview.skipped_manual.length}</div>
              </div>
              <div className="bg-white p-2 rounded border">
                <div className="text-xs text-slate-500">✓ Không đổi</div>
                <div className="text-2xl font-bold text-slate-500">{litellmPreview.unchanged_count}</div>
              </div>
            </div>

            {litellmPreview.changed.length > 0 && (
              <details className="mt-2">
                <summary className="cursor-pointer text-sm font-medium text-blue-700">
                  Chi tiết {litellmPreview.changed.length} model đổi giá ▾
                </summary>
                <div className="max-h-64 overflow-auto mt-2 text-xs">
                  <table className="w-full">
                    <thead className="text-left">
                      <tr className="border-b">
                        <th className="p-1">Model</th>
                        <th className="p-1">In cũ → mới</th>
                        <th className="p-1">Out cũ → mới</th>
                      </tr>
                    </thead>
                    <tbody>
                      {litellmPreview.changed.slice(0, 50).map((c, i) => (
                        <tr key={i} className="border-b">
                          <td className="p-1 font-mono">{c.provider}/{c.model}</td>
                          <td className="p-1">{fmtUsd(c.old_in)} → <strong>{fmtUsd(c.price_per_1k_in)}</strong> {c.diff_pct_in != null && <span className={c.diff_pct_in > 0 ? 'text-red-600' : 'text-emerald-600'}>({c.diff_pct_in > 0 ? '+' : ''}{c.diff_pct_in.toFixed(0)}%)</span>}</td>
                          <td className="p-1">{fmtUsd(c.old_out)} → <strong>{fmtUsd(c.price_per_1k_out)}</strong> {c.diff_pct_out != null && <span className={c.diff_pct_out > 0 ? 'text-red-600' : 'text-emerald-600'}>({c.diff_pct_out > 0 ? '+' : ''}{c.diff_pct_out.toFixed(0)}%)</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {litellmPreview.changed.length > 50 && (
                    <div className="text-center text-slate-500 py-2">... và {litellmPreview.changed.length - 50} model khác</div>
                  )}
                </div>
              </details>
            )}

            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={applyLitellmSync}
                disabled={busy || (litellmPreview.added.length === 0 && litellmPreview.changed.length === 0)}
                className="px-4 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50"
              >
                Áp dụng ({litellmPreview.added.length + litellmPreview.changed.length} thay đổi)
              </button>
              <button
                type="button"
                onClick={() => setLitellmPreview(null)}
                className="px-4 py-1.5 rounded-md bg-slate-200 hover:bg-slate-300 text-sm"
              >
                Huỷ
              </button>
            </div>
          </div>
        )}

        {/* Add/Edit form */}
        {showAddForm && (
          <form onSubmit={submitForm} className="mb-4 p-4 bg-slate-50 border rounded-md space-y-3">
            <h3 className="font-semibold">{editingId ? 'Sửa giá model' : 'Thêm model mới'}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Provider</label>
                <select
                  value={form.provider}
                  onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}
                  disabled={!!editingId}
                  className="w-full px-3 py-2 border rounded-md disabled:bg-slate-100"
                >
                  {PROVIDER_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Model name</label>
                <input
                  type="text"
                  value={form.model}
                  onChange={e => setForm(f => ({ ...f, model: e.target.value }))}
                  disabled={!!editingId}
                  placeholder="claude-haiku-4-5"
                  className="w-full px-3 py-2 border rounded-md disabled:bg-slate-100"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Giá input (USD / 1k tokens)</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={form.price_per_1k_in}
                  onChange={e => setForm(f => ({ ...f, price_per_1k_in: e.target.value }))}
                  placeholder="0.001"
                  className="w-full px-3 py-2 border rounded-md font-mono"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Giá output (USD / 1k tokens)</label>
                <input
                  type="number"
                  step="any"
                  min="0"
                  value={form.price_per_1k_out}
                  onChange={e => setForm(f => ({ ...f, price_per_1k_out: e.target.value }))}
                  placeholder="0.005"
                  className="w-full px-3 py-2 border rounded-md font-mono"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-slate-600 mb-1">Ghi chú (optional)</label>
                <input
                  type="text"
                  value={form.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="VD: Nhanh, rẻ, dùng cho ask_ai"
                  className="w-full px-3 py-2 border rounded-md"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-1">
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium disabled:opacity-50"
              >
                {editingId ? 'Lưu' : 'Thêm'}
              </button>
              <button
                type="button"
                onClick={cancelForm}
                className="px-4 py-2 rounded-md bg-slate-200 hover:bg-slate-300 text-sm"
              >
                Huỷ
              </button>
            </div>
          </form>
        )}

        {/* Pricing table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 text-left">
              <tr>
                <th className="p-2 font-semibold">Provider</th>
                <th className="p-2 font-semibold">Model</th>
                <th className="p-2 font-semibold text-right">Giá input ($/1k)</th>
                <th className="p-2 font-semibold text-right">Giá output ($/1k)</th>
                <th className="p-2 font-semibold text-right">VND/1k in*</th>
                <th className="p-2 font-semibold">Nguồn</th>
                <th className="p-2 font-semibold">Sync cuối</th>
                <th className="p-2 font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {data.pricing.map(row => {
                const src = SOURCE_LABEL[row.source] || SOURCE_LABEL.manual;
                const vndPerKIn = Math.round(Number(row.price_per_1k_in) * data.usd_to_vnd.value);
                return (
                  <tr key={row.id} className="border-b hover:bg-slate-50">
                    <td className="p-2 font-mono text-xs">{row.provider}</td>
                    <td className="p-2 font-mono text-xs">{row.model}</td>
                    <td className="p-2 text-right font-mono">{fmtUsd(row.price_per_1k_in)}</td>
                    <td className="p-2 text-right font-mono">{fmtUsd(row.price_per_1k_out)}</td>
                    <td className="p-2 text-right text-slate-500 text-xs">{fmtVnd(vndPerKIn)}</td>
                    <td className="p-2"><span className={`inline-block px-2 py-0.5 rounded text-xs border ${src.color}`}>{src.text}</span></td>
                    <td className="p-2 text-xs text-slate-500">{fmtDt(row.last_sync_at)}</td>
                    <td className="p-2">
                      <div className="flex gap-1 justify-end">
                        <button
                          type="button"
                          onClick={() => startEdit(row)}
                          className="px-2 py-1 text-xs rounded hover:bg-blue-100 text-blue-700"
                        >
                          Sửa
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(row)}
                          className="px-2 py-1 text-xs rounded hover:bg-red-100 text-red-700"
                        >
                          Xoá
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-xs text-slate-500 mt-2">* VND/1k in = giá USD × tỷ giá USD/VND hiện tại — chỉ tham khảo nhanh.</div>
      </section>
    </div>
  );
}
